import type { FormApi } from "final-form";
// constants
import {
  CONTACT_FIELDS,
  CONTACT_MEDIUM_FIELDS,
  CONTACT_NAME_FIELDS,
  PAX_FORM_ID,
  PAX_SUB_FORM_BUTTON_ID,
  PAX_SUB_FORM_ID,
} from "@/constants/common.constants";

// utils
import _get from "lodash/get";
import _keyBy from "lodash/keyBy";
import _filter from "lodash/filter";
import _values from "lodash/values";
import _isEqual from "lodash/isEqual";
import _size from "lodash/size";
import _forEach from "lodash/forEach";
import _times from "lodash/times";
import _reduce from "lodash/reduce";
import _isEmpty from "lodash/isEmpty";
import _some from "lodash/some";
import _isFunction from "lodash/isFunction";
import _isPlainObject from "lodash/isPlainObject";
import _keys from "lodash/keys";
import _noop from "lodash/noop";
import _pick from "lodash/pick";
import _find from "lodash/find";
import _isUndefined from "lodash/isUndefined";
import { format, isValid } from "date-fns";
import { scrollToElement } from "@/utils/helpers.utils";
import {
  getBaggageFieldName,
  getContactFromUser,
  getExtraBaggage,
  isAdultTraveller,
} from "@/utils/compute.utils";
import {
  CachedTraveller,
  SavedTravellerModel,
  TravellerModel,
} from "@tajawal/web-flights-sdk/lib/types/traveller.types";
import { isValidContactValue } from "@tajawal/web-flights-sdk/lib/utils/pax.utils";
import { updateTravellerState } from "@tajawal/web-flights-sdk/lib/utils/traveller.utils";
import { getRequiredFields } from "./validation.utils";
import _toLower from "lodash/toLower";
import {
  ContactMediumCache,
  getBookingForOthersSessionState,
  TravellerCache,
} from "./cache.utils";
import { PaxType } from "@tajawal/web-flights-sdk/lib/types/common.types";
import { FieldRenderProps } from "react-final-form";
import { Dictionary } from "lodash";
import { SelectedLuggageModel } from "@tajawal/web-flights-sdk/lib/types/luggage.types";
import { TFunction } from "i18next";
import { DATE_FORMATS } from "@/constants/date.constants";
import { UNACCOMPANIED_MINOR_AIRLINES } from "@tajawal/web-flights-sdk/lib/constants";

export function handleFormSubmit() {
  const ctaBtn = document.getElementById(PAX_FORM_ID);

  let event;
  if (typeof Event === "function") {
    event = new Event("submit", { cancelable: true, bubbles: true });
  } else {
    event = document.createEvent("Event");
    event.initEvent("submit", true, true);
  }
  event.preventDefault();

  ctaBtn.dispatchEvent(event);
}

export function handleSubFormSubmit() {
  const ctaBtn = document.getElementById(PAX_SUB_FORM_BUTTON_ID);
  ctaBtn?.click();
}

export function getFormStateValues(
  path: string,
  fieldsState,
): Record<string, any> {
  const result = {};

  Object.keys(fieldsState).forEach((name) => {
    const key = typeof name === "string" ? name.split(".").pop() : null;
    const value = fieldsState[name];

    if (key) {
      result[key] = _get(value, path);
    }
  });

  return result;
}

export function resetFormDateFieldValue(form: FormApi, name: string) {
  form.change(name, new Date("Invalid date"));
  form.change(`${name}Day`, "");
  form.change(`${name}Month`, "");
  form.change(`${name}Year`, "");
}

export function getFormDateFieldValues(form: FormApi, name: string) {
  const { value: day } = form.getFieldState(`${name}Day`) || {};
  const { value: month } = form.getFieldState(`${name}Month`) || {};
  const { value: year } = form.getFieldState(`${name}Year`) || {};

  return { day, month, year };
}

// TODO update fieldsState type
export function isFullNameFocused(fieldsState: any) {
  const activeState = getFormStateValues("meta.active", fieldsState);
  const title = _get(activeState, "title", false) ? "title" : "";
  const firstName = _get(activeState, "firstName", false) ? "firstName" : "";
  const middleName = _get(activeState, "middleName", false) ? "middleName" : "";
  const lastName = _get(activeState, "lastName", false) ? "lastName" : "";
  return title || firstName || middleName || lastName;
}

/**
 * get IDTypes options with new structure for CustomSelectInput
 */
export function getIdTypesOptions({
  documentTypes,
  t,
}: {
  documentTypes: any[];
  t: any;
}) {
  return _reduce(
    documentTypes,
    (result, type) => {
      return {
        ...result,
        [type]: { code: type, label: t(`Pax.TravellerForm_IDTypes_${type}`) },
      };
    },
    {},
  );
}

/**
 * list of fields to validate in order to determine whether the traveller form is valid. Providing contactFieldName function will cause contact fields to be included in the result as well
 */
export function getValidationFields(
  form: FormApi,
  fieldName: (string) => string,
  contactFieldName?: (string) => string,
) {
  const requiredFields = [
    fieldName("title"),
    fieldName("firstName"),
    fieldName("middleName"),
    fieldName("lastName"),
  ];

  const isDateOfBirthRequired = _get(
    form.getState(),
    `values.${fieldName("isDateOfBirthRequired")}`,
    true,
  );
  const isIDRequired = _get(
    form.getState(),
    `values.${fieldName("isIDRequired")}`,
    true,
  );
  const isAPIsRequired = _get(
    form.getState(),
    `values.${fieldName("isAPIsRequired")}`,
    true,
  );

  if (isDateOfBirthRequired) {
    requiredFields.push(
      fieldName("birthDate"),
      fieldName("birthDateDay"),
      fieldName("birthDateMonth"),
      fieldName("birthDateYear"),
    );
  }

  if (isAPIsRequired || isIDRequired) {
    requiredFields.push(fieldName("nationality"));
  }

  if (isIDRequired) {
    requiredFields.push(
      fieldName("idType"),
      fieldName("idNumber"),
      fieldName("idExpiryDate"),
      fieldName("idExpiryDateDay"),
      fieldName("idExpiryDateMonth"),
      fieldName("idExpiryDateYear"),
      fieldName("issuingCountry"),
    );
  }

  if (_isFunction(contactFieldName)) {
    requiredFields.push(
      contactFieldName("title"),
      contactFieldName("firstName"),
      contactFieldName("lastName"),
      contactFieldName("email"),
      contactFieldName("phone"),
      contactFieldName("dialCode"),
    );
  }

  return requiredFields;
}

export function updateValidField(
  form: FormApi,
  fieldName: (string) => string,
  contactFieldName: (string) => string,
) {
  const fields = getValidationFields(form, fieldName, contactFieldName);
  const hasError = fields.some((field) => {
    const fieldState = form.getFieldState(field);
    return !fieldState || _get(fieldState, "error", "");
  });
  form.change(fieldName("isValid"), !hasError);
}

function hashCode(string: string) {
  let hash = 0;
  let i;
  let chr;
  if (string.length === 0) return hash;
  for (i = 0; i < string.length; i++) {
    chr = string.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return hash;
}

export function updateTravellerFormHash({
  form,
  parentForm,
  travellerFormId,
  title,
  firstName,
  lastName,
  birthDate,
}: {
  form: FormApi;
  parentForm;
  travellerFormId: string;
  title?: string;
  firstName?: string;
  lastName?: string;
  birthDate?: string;
}) {
  const newTitle = title || _get(form.getFieldState("title"), "value", "");
  const fName = firstName || _get(form.getFieldState("firstName"), "value", "");
  const lName = lastName || _get(form.getFieldState("lastName"), "value", "");
  const dob = birthDate || _get(form.getFieldState("birthDate"), "value", "");
  const parsedDate = new Date(dob);
  const bDate = isValid(parsedDate) ? format(parsedDate, "ddMMyyyy") : "";
  const hashKey = _toLower(`${newTitle}${fName}${lName}${bDate}`);
  const hash = hashCode(hashKey);
  const current = _get(parentForm.getState(), "values.travellersFormHash", {});
  parentForm.change("travellersFormHash", {
    ...current,
    [travellerFormId]: hash,
  });
}

export function getTravellerHashKey(
  traveller: TravellerModel | SavedTravellerModel,
) {
  const title = _get(traveller, "title", "");
  const firstName = _get(traveller, "firstName", "");
  const lastName = _get(traveller, "lastName", "");
  let birthDate = _get(traveller, "birthDate", "");
  const parsedDate = new Date(birthDate);
  birthDate = isValid(parsedDate) ? format(parsedDate, "ddMMyyyy") : "";
  return _toLower(`${title}${firstName}${lastName}${birthDate}`);
}

export function getTravellerHashCode(traveller) {
  return hashCode(getTravellerHashKey(traveller));
}

export function setSavedTravellersHash(
  parentForm: FormApi,
  form: FormApi,
  savedTravellers: SavedTravellerModel[],
  type: PaxType,
) {
  const currentHash = _get(
    parentForm.getState(),
    "values.savedTravellersHash",
    {},
  );

  if (_isEmpty(currentHash[type] && _size(savedTravellers) > 0)) {
    const savedTravellersHash = _reduce(
      savedTravellers,
      (updatedHash, traveller) => {
        const hashKey = getTravellerHashKey(traveller);
        return { ...updatedHash, [hashKey]: hashCode(hashKey) };
      },
      {},
    );

    const newHash = {
      ...currentHash,
      [type]: savedTravellersHash,
    };
    parentForm.change("savedTravellersHash", newHash);
    form.change("savedTravellersHash", newHash);
  }
}

/**
 * returns date string (Gregorian or Hijri, for example '18 December 1997')
 */
export function getTravellerDateOfBirth(traveller) {
  const parsedDate = new Date(_get(traveller, "birthDate", undefined));
  if (!isValid(parsedDate)) return "";

  return format(parsedDate, "dd/MM/yyyy");
}

/**
 * Scrolls to the first invalid traveller, starting from traveller with the
 * specified index. All other traveller forms are minimized in the process.
 */
export function scrollToTraveller({
  index,
  form,
}: {
  index: number;
  form: FormApi;
}) {
  const travellers = _get(form.getState(), "values.travellers", []);
  let currentIndex = index % travellers.length;
  let expandedIndex;
  let invalidFormFound = false;
  _times(travellers.length, () => {
    const isValid = travellers[currentIndex].isValid;
    form.change(
      `travellers.${currentIndex}.isExpanded`,
      !isValid && !invalidFormFound,
    );
    if (!isValid && !invalidFormFound) {
      expandedIndex = currentIndex;
      invalidFormFound = true;
    }
    currentIndex = (currentIndex + 1) % travellers.length;
  });

  if (invalidFormFound) {
    setTimeout(
      () =>
        scrollToElement({
          id: `travellers.${expandedIndex}.headerLabel`,
          yOffset: -100,
        }),
      100,
    );
  } else {
    setTimeout(
      () => scrollToElement({ id: `travellers.0.headerLabel`, yOffset: -170 }),
      100,
    );
  }
}

/**
 * A final-form decorator to expand all invalid traveller forms (and minimize valid ones) upon submission
 */
export function expandOnError(formObj: FormApi) {
  // this assignment is necessary to avoid ESLint error: no∏-param-reassign
  const form = formObj;
  const originalSubmit = form.submit;

  const unsubscribe = form.subscribe(() => {}, { submitFailed: true });

  const afterSubmit = () => {
    const travellers = _get(form.getState(), "values.travellers", []);
    travellers.forEach((traveller, i) => {
      form.change(`travellers.${i}.isExpanded`, !traveller.isValid);
    });

    const delay = 100;
    return new Promise((res) => setTimeout(res, delay));
  };

  form.submit = () => {
    const result = originalSubmit.call(form);
    if (result && typeof result.then === "function") {
      return result.then(afterSubmit);
    }
    return afterSubmit();
  };

  return () => {
    unsubscribe();
    form.submit = originalSubmit;
  };
}

export function updateUnaccompaniedMinor({
  form,
  travellers = [],
  isAdult,
  startDate,
}: {
  form: FormApi;
  travellers: TravellerModel[];
  isAdult?: boolean;
  startDate: Date;
}): void {
  if (isAdult) {
    const hasAdult = checkForAdultTraveller(travellers, startDate);
    form.change("hasUnaccompaniedMinor", !hasAdult);
  }
}

export function checkForAdultTraveller(
  travellers: TravellerModel[],
  startDate: Date,
) {
  return _some(
    travellers,
    (traveller) =>
      traveller.isAdult &&
      (!isValid(traveller.birthDate) ||
        isAdultTraveller(
          startDate,
          traveller.birthDate,
          traveller.birthDateIsHijri,
        )),
  );
}

export function fillContactField({
  form,
  field,
  contact,
}: {
  form: FormApi;
  field: string;
  contact: Record<string, any>;
}) {
  const value = _get(contact, field);
  if (isValidContactValue(field, value)) {
    form.change(`contact.${field}`, value);
    return true;
  }
  return false;
}

export function fillContact({
  form,
  contact,
}: {
  form: FormApi;
  contact: Record<string, any>;
}) {
  if (!_isPlainObject(contact)) {
    return false;
  }
  let isFullNameUpdated = false;

  CONTACT_FIELDS.forEach((field) => {
    const isFieldUpdated = fillContactField({ field, form, contact });

    if (["firstName", "lastName"].includes(field)) {
      isFullNameUpdated = isFullNameUpdated || isFieldUpdated;
    }
    if (field === "fullName") {
      fillFullName({ form, contact });
    }
  });

  return isFullNameUpdated;
}

export function fillFullName({
  form,
  contact,
}: {
  form: FormApi;
  contact: Record<string, any>;
}): void {
  const { firstName, lastName } = contact;
  form.change("contact.fullName", `${firstName} ${lastName}`.trim());
}

export function getHelperText(
  error: string,
  shouldShowError: boolean,
  helperText?: string,
): string {
  if (shouldShowError) {
    return error;
  }

  return helperText || "";
}

export function getTravellersFromForm(
  mainForm: FormApi,
  form: FormApi,
  travellerIndex: number,
): TravellerModel[] {
  const travellers: TravellerModel[] = [
    ...(mainForm.getState()?.values?.travellers || []),
  ];
  travellers[travellerIndex] = form.getState()?.values as TravellerModel;
  return travellers;
}

export function prefillTravellerFromCache(
  form: FormApi,
  fieldName: (key: string) => string,
  traveller: TravellerModel,
  cachedTraveller: CachedTraveller,
): void {
  const travellerValues = updateTravellerState(traveller, cachedTraveller);
  const keys = _keys(travellerValues);
  const requiredFields = getRequiredFields(traveller);
  const isFormValid = keys.reduce((reducer, key) => {
    if (key === "products") return reducer;
    if (
      form.getRegisteredFields().includes(fieldName(key)) &&
      !!travellerValues[key]
    ) {
      form.change(fieldName(key), travellerValues[key]);
    } else {
      form.registerField(
        fieldName(key),
        _noop,
        { value: travellerValues[key] },
        { initialValue: travellerValues[key] },
      );
    }
    if (requiredFields.includes(key)) {
      if (_toLower(key).includes("date")) {
        return (
          !!travellerValues[key] && isValid(travellerValues[key]) && reducer
        );
      }
      return !!travellerValues[key] && reducer;
    }
    return reducer;
  }, true);

  form.change(fieldName("isValueSavedOnce"), isFormValid);
}

export function prefillContact(contact, prefillInfo) {
  const contactNameFields = _pick(prefillInfo, CONTACT_NAME_FIELDS);
  return {
    ...contact,
    ...contactNameFields,
  };
}

export function formatContact(contact) {
  const phone = _get(contact, "phone", "");
  return { ...contact, phone: phone.replace(/\s|-|_/g, "") };
}

export function isContactValid(contact, fieldsToValidate) {
  if (_isPlainObject(contact)) {
    return fieldsToValidate.some((field) =>
      isValidContactValue(field, contact[field]),
    );
  }
  return false;
}

export function resetFieldTouchedState(fieldName) {
  return function (formState) {
    const fieldsToResetState = ["phone", "email"];
    _forEach(fieldsToResetState, (field) => {
      formState.fields[fieldName(field)].touched = false;
    });
  };
}

export function isSavedContact({
  firstName,
  lastName,
}: {
  firstName: string;
  lastName: string;
}) {
  return (
    (!_isUndefined(firstName) && _size(firstName) > 0) ||
    (!_isUndefined(lastName) && _size(lastName) > 0)
  );
}

export function clearContact(form, contact) {
  _forEach(CONTACT_FIELDS, (field) => {
    if (field !== "isBookingForOthers") {
      const value = ["dialCode", "countryId"].includes(field)
        ? contact[field]
        : "";
      form.change(`contact.${field}`, value);
    }
  });
}

export function prefillContactFromSavedTraveller(
  form,
  savedTraveller,
  fieldName,
) {
  const keys = _keys(savedTraveller);
  CONTACT_NAME_FIELDS.forEach((field) => {
    if (keys.includes(field)) {
      form.change(fieldName(field), savedTraveller[field]);
    }
  });
}

export function resetBookingForOthers(form, fieldName) {
  form.change(fieldName("isBookingForOthers"), false);
  form.change(
    fieldName("isBookingForOthers"),
    getBookingForOthersSessionState(),
  );
}

export function getInitialCacheData({
  isUser,
  user,
  traveller,
  contact,
  travellers,
  lastDepartureDate,
  index,
  carriers,
}) {
  const cachedTraveller = TravellerCache.get()?.[traveller.id];

  const cachedTravellerUpdated = updateTravellerState(
    traveller,
    cachedTraveller,
  );

  const isTravellerNamesValid = isContactValid(
    cachedTravellerUpdated,
    CONTACT_NAME_FIELDS,
  );
  const cachedContactMedium = ContactMediumCache.get();
  const isMediumValid = isContactValid(
    cachedContactMedium,
    CONTACT_MEDIUM_FIELDS,
  );
  const userData = isUser ? getContactFromUser(user) : {};
  const cachedContactUpdated = prefillContact(
    isMediumValid ? cachedContactMedium : userData,
    isTravellerNamesValid ? cachedTravellerUpdated : {},
  );
  const updatedTravellers = [...travellers];
  updatedTravellers[index] = cachedTravellerUpdated;

  const isUnaccompaniedMinorAirline =
    Array.isArray(carriers) &&
    UNACCOMPANIED_MINOR_AIRLINES.some((code) => carriers.includes(code));

  return {
    traveller: {
      ...cachedTravellerUpdated,
      isUnaccompaniedMinor:
        cachedTravellerUpdated.isAdult &&
        isUnaccompaniedMinorAirline &&
        !checkForAdultTraveller(updatedTravellers, new Date(lastDepartureDate)),
    },
    contact: formatContact({ ...contact, ...cachedContactUpdated }),
  };
}

export function focusOnFirstError(errors: Record<string, string>): void {
  const form = document.getElementById(PAX_SUB_FORM_ID);
  const inputs = form.getElementsByTagName("input");
  const firstErrorInput = _find(
    inputs,
    (input) => input.name && _get(errors, input.name),
  ) as HTMLInputElement;

  if (firstErrorInput) {
    firstErrorInput.focus();
    firstErrorInput.scrollIntoView();
  }
}

function getIdFilteredBaggage(
  baggage: Dictionary<SelectedLuggageModel>,
): Dictionary<SelectedLuggageModel> {
  return _keyBy(
    _filter(_values(baggage), Boolean) as SelectedLuggageModel[],
    "id",
  );
}

export function getIsBaggageSaveEnabled({
  isMultiPNR,
  traveller,
  fieldsState,
}: {
  isMultiPNR: boolean;
  traveller: TravellerModel;
  fieldsState: Record<string, FieldRenderProps<HTMLInputElement>>;
}): boolean {
  if (!isMultiPNR) {
    return !_isEqual(
      getIdFilteredBaggage(_get(traveller, "ssr.luggage")),
      getIdFilteredBaggage(_get(fieldsState, "ssr.luggage")?.input?.value),
    );
  }
  return _some(traveller?.products, (_, productId) => {
    const fieldValue =
      fieldsState[getBaggageFieldName(productId, isMultiPNR)]?.input?.value;
    const initialValue = getExtraBaggage(traveller, isMultiPNR, productId);
    return !_isEqual(
      getIdFilteredBaggage(initialValue),
      getIdFilteredBaggage(fieldValue),
    );
  });
}

export function getFormAccordionSubTitle(date: string, t: TFunction) {
  const dob = new Date(date);
  if (isValid(dob)) {
    return format(dob, DATE_FORMATS.DATE_OF_BIRTH_DISPLAY);
  }
  return t("Pax.TravellerForm_Notice");
}
